import { Link, useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { AlertTriangle, TrendingDown, ClipboardCheck, FileWarning } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, DATA_ISSUE_LABELS } from "../utils/constants";
import { useDashboard } from "../hooks/useAssets";
import { Spinner, Badge } from "../components/ui/index.jsx";
import { formatDate, formatRelative, formatIDR } from "../utils/format";

// Single-hue bar chart, nominal categories (entity/category names have no
// natural order) — every bar takes the same slot, no per-bar color, no
// legend needed for one series. See dataviz skill: "nominal categorical...
// each bar takes the same slot-1 hue."
const CHART_COLOR = "#2a78d6";
const CHART_TOP_N = 8;

// ---------------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------------
function StatTile({ label, value, to, hint }) {
  const body = (
    <>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label}
      </p>
      <p className="mt-2 text-3xl font-bold text-slate-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </>
  );
  const shell = "bg-white rounded-xl border border-slate-200 p-5";
  // A tile with a destination is a link into the pre-filtered asset list
  return to ? (
    <Link
      to={to}
      className={`${shell} block hover:border-primary-300 hover:bg-slate-50 transition-colors`}
    >
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  );
}

// ---------------------------------------------------------------------------
// List card — shared shell for Warranty Deadlines / Depreciation / Recent Log.
// Each row links to that asset's detail page (there's no list-level filter
// for warranty-status/depreciation/activity-type yet, so a "View all" link
// to a filtered list isn't possible here — see CONTEXT.md Dashboard section).
// ---------------------------------------------------------------------------
function ListCard({ icon: Icon, title, emptyText, items, renderRow, footer }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-slate-400" />
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {title}
        </h2>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-4 text-center">
          {emptyText}
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((item) => renderRow(item))}
        </div>
      )}
      {footer && <div className="mt-3 pt-3 border-t border-slate-100">{footer}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart card
// ---------------------------------------------------------------------------
function ChartCard({ title, data, dataKey, labelKey, onBarClick }) {
  const top = [...data].sort((a, b) => b.count - a.count).slice(0, CHART_TOP_N);
  const restCount = data
    .slice(CHART_TOP_N)
    .reduce((sum, d) => sum + d.count, 0);
  const chartData =
    data.length > CHART_TOP_N
      ? [...top, { [labelKey]: "Other", count: restCount, isOther: true }]
      : top;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">
        {title}
      </h2>
      {data.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-8 text-center">
          No assets yet.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ left: -20 }}>
            <CartesianGrid vertical={false} stroke="#e2e8f0" />
            <XAxis
              dataKey={labelKey}
              tick={{ fontSize: 11, fill: "#64748b" }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
              width={30}
            />
            <Tooltip
              cursor={{ fill: "#f1f5f9" }}
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            />
            <Bar
              dataKey={dataKey}
              fill={CHART_COLOR}
              radius={[4, 4, 0, 0]}
              barSize={24}
              onClick={(entry) => !entry.isOther && onBarClick?.(entry)}
              cursor={onBarClick ? "pointer" : "default"}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useDashboard();

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          Welcome back, {user?.name}. You are logged in as{" "}
          <span className="font-medium text-slate-700">
            {ROLE_LABELS[user?.role] ?? user?.role}
          </span>
          .
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="text-center py-20 text-rose-600 text-sm">
          Failed to load dashboard data. Please refresh.
        </div>
      ) : (
        <div className="space-y-5">
          {/* Stat tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatTile
              label="Total Assets"
              value={data.stats.totalAssets.toLocaleString()}
            />
            <StatTile
              label="Active Holders"
              value={data.stats.activeHolders.toLocaleString()}
            />
            <StatTile
              label="Warranty Expiring Soon"
              value={data.stats.warrantyExpiringSoon.toLocaleString()}
            />
            <StatTile
              label="Entities"
              value={data.stats.totalEntities.toLocaleString()}
            />
            <StatTile
              label="Data Issues"
              value={(data.stats.flaggedAssets ?? 0).toLocaleString()}
              hint="assets still flagged — open the list"
              to="/assets?dataIssue=ANY"
            />
          </div>

          {/* Warranty + Depreciation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ListCard
              icon={AlertTriangle}
              title="Warranty Deadlines"
              emptyText="No warranties expiring or recently expired."
              footer={
                data.stats.warrantyExpiredLong > 0 && (
                  <Link
                    to="/assets?warranty=EXPIRED_LONG"
                    className="text-xs text-primary-600 hover:underline"
                  >
                    View {data.stats.warrantyExpiredLong} long-expired (over 14
                    days) →
                  </Link>
                )
              }
              items={data.warrantyDeadlines}
              renderRow={(a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/assets/${a.id}`)}
                  className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {a.name}
                    </p>
                    <p className="text-xs text-slate-400 font-mono">
                      {a.assetNumber}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant={a.status === "EXPIRED" ? "danger" : "warning"}>
                      {a.status === "EXPIRED"
                        ? `Expired ${Math.abs(a.daysRemaining)}d ago`
                        : `${a.daysRemaining}d left`}
                    </Badge>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatDate(a.warrantyEndDate)}
                    </p>
                  </div>
                </button>
              )}
            />

            <ListCard
              icon={TrendingDown}
              title="Depreciation Near End-of-Life"
              emptyText="No assets nearing full depreciation."
              items={data.depreciationNearEndOfLife}
              renderRow={(a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/assets/${a.id}`)}
                  className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {a.name}
                    </p>
                    <p className="text-xs text-slate-400 font-mono">
                      {a.assetNumber}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant="warning">
                      {a.monthsRemaining} {a.monthsRemaining === 1 ? "month" : "months"} left
                    </Badge>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatIDR(a.bookValue)} book value
                    </p>
                  </div>
                </button>
              )}
            />
          </div>

          {/* Assets that still have data-issue flags */}
          <ListCard
            icon={FileWarning}
            title="Needs Attention — Data Issues"
            emptyText="No assets have open data issues."
            items={data.flaggedAssetList ?? []}
            renderRow={(a) => (
              <button
                key={a.id}
                onClick={() => navigate(`/assets/${a.id}`)}
                className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {a.name}
                  </p>
                  <p className="text-xs text-slate-400 font-mono">
                    {a.assetNumber}
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[55%]">
                  {a.dataIssues.map((i) => (
                    <Badge key={i} variant="warning">
                      {DATA_ISSUE_LABELS[i] ?? i}
                    </Badge>
                  ))}
                </div>
              </button>
            )}
          />
          {data.stats.flaggedAssets > (data.flaggedAssetList?.length ?? 0) && (
            <p className="-mt-3 text-right text-xs">
              <Link
                to="/assets?dataIssue=ANY"
                className="text-primary-600 hover:underline"
              >
                View all {data.stats.flaggedAssets} flagged assets →
              </Link>
            </p>
          )}

          {/* Recent Asset Log */}
          <ListCard
            icon={ClipboardCheck}
            title="Recent Asset Log"
            emptyText="No inspections logged yet."
            items={data.recentActivityLog}
            renderRow={(log) => (
              <button
                key={log.id}
                onClick={() => log.assetId && navigate(`/assets/${log.assetId}`)}
                className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {log.assetName ?? "Unknown asset"}
                  </p>
                  <p className="text-xs text-slate-400">
                    {log.performerName} · {formatRelative(log.occurredAt)}
                  </p>
                </div>
                <Badge
                  variant={
                    log.condition === "Good"
                      ? "success"
                      : log.condition === "Damaged"
                        ? "danger"
                        : "warning"
                  }
                >
                  {log.condition}
                </Badge>
              </button>
            )}
          />

          {/* Breakdown charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard
              title="Assets by Entity"
              data={data.byEntity}
              dataKey="count"
              labelKey="code"
              onBarClick={(entry) =>
                navigate(`/assets?entityId=${entry.entityId}`)
              }
            />
            <ChartCard
              title="Assets by Category"
              data={data.byCategory}
              dataKey="count"
              labelKey="code"
              onBarClick={(entry) =>
                entry.categoryId &&
                navigate(`/assets?categoryId=${entry.categoryId}`)
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
